import { Chat, Query } from "@/app/graphql/codegen";
import { Grid2, Typography, Skeleton } from "@mui/material";
import ChatFilter from "../ChatFilter";
import SelectChatButton from "@chat/components/SelectChatButton";
import { availableViewportHeight } from "@core/layout/AppLayout";
import { FETCH_CHATS } from "@chat/graphql/queries/fetchChats";
import { useChatSelectionSlice } from "@store/slices/chatSelection";
import { useViewSlice } from "@store/slices/view";
import { useQuery } from "@apollo/client";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import { useEffect, useMemo, useState } from "react";
import debounce from "lodash/debounce";

export const ChatList = () => {
    const { id, sortOrder, sortField, searchTerm, visibility } = useChatSelectionSlice();
    const { showExtendedView } = useViewSlice();
    
    // Use state for debounced search term
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
    
    // Create debounced function with lodash
    const debouncedSetSearchTerm = useMemo(
        () => debounce(value => {
            setDebouncedSearchTerm(value);
        }, 400),
        []
    );
    
    // Update debounced value when searchTerm changes
    useEffect(() => {
        debouncedSetSearchTerm(searchTerm);
        return () => {
            debouncedSetSearchTerm.cancel();
        };
    }, [searchTerm, debouncedSetSearchTerm]);
    
    // Query variables for Apollo with debounced search term
    const queryVariables = useMemo(() => ({
        searchTerm: debouncedSearchTerm,
        visibility,
        sortField,
        sortOrder
    }), [debouncedSearchTerm, visibility, sortField, sortOrder]);

    // Use Apollo query with variables
    const { data, loading } = useQuery<Pick<Query, "chats">>(FETCH_CHATS, {
        variables: queryVariables,
        fetchPolicy: "cache-and-network"
    });
    
    const chats = data?.chats ?? [];
    
    // No need to filter/sort chats again since backend does that now
    const favoriteChats = chats.filter((chat) => chat.isFavorite);
    const skeletonHeight = 42;

    // Only show loading when we don't have any data yet
    const shouldShowLoading = loading && chats.length === 0;

    return (
        <Grid2 container size={12} p={1}>
            <SimpleTreeView 
                defaultExpandedItems={["favorites", "documents"]} 
                sx={{ width: "100%" }}
                onKeyDownCapture={(e) => {
                    // Disable type-ahead focusing (F key navigation to "Favoriten")
                    // Only stop single character keys that would trigger type-ahead
                    const isTypeAheadKey = e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
                    if (isTypeAheadKey) {
                        e.stopPropagation();
                    }
                }}
            >
                <TreeItem 
                    itemId={"favorites"} 
                    label={<Typography variant={"h5"}>Favoriten</Typography>}
                >
                    <Grid2 container size={12} spacing={1.25} pt={1.5} pb={3.5}>
                        {shouldShowLoading ? (
                            Array.from({ length: 3 }).map((_, index) => (
                                <Grid2 size={12} key={`skeleton-fav-${index}`}>
                                    <Skeleton height={skeletonHeight} />
                                </Grid2>
                            ))
                        ) : (
                            favoriteChats.map((chat: Chat) => (
                                <Grid2 size={12} key={chat.id}>
                                    <SelectChatButton 
                                        name={chat.name || "Unnamed Chat"} 
                                        id={chat.id} 
                                        selected={chat.id === id} 
                                    />
                                </Grid2>
                            ))
                        )}
                    </Grid2>
                </TreeItem>

                <TreeItem 
                    itemId={"documents"} 
                    label={<Typography variant={"h5"}>Chats</Typography>}
                >
                    <Grid2 container size={12} spacing={2.5} mt={0.75}>
                        <Grid2 size={12} mb={-0.75}>
                            <ChatFilter />
                        </Grid2>

                        <Grid2 container size={12} spacing={1.25}>
                            {shouldShowLoading ? (
                                Array.from({ length: 7 }).map((_, index) => (
                                    <Grid2 size={12} key={`skeleton-${index}`}>
                                        <Skeleton height={skeletonHeight} />
                                    </Grid2>
                                ))
                            ) : (
                                chats.map((chat: Chat) => (
                                    <Grid2 size={12} key={chat.id}>
                                        <SelectChatButton 
                                            name={chat.name || "Unnamed Chat"} 
                                            id={chat.id} 
                                            selected={chat.id === id} 
                                        />
                                    </Grid2>
                                ))
                            )}
                        </Grid2>
                    </Grid2>
                </TreeItem>
            </SimpleTreeView>
        </Grid2>
    );
};